import { Helmet } from 'react-helmet-async';
// @mui
import {
    Card,
    Stack,
    Container,
    Typography,
    TableContainer,
    CardContent,
    Button,
    CircularProgress,
    Box,
    AccordionSummary,
    AccordionDetails,
    Accordion,
    Paper,
    Checkbox, FormControlLabel,
} from '@mui/material';

import {useNavigate, useParams} from 'react-router-dom';

// components
import Scrollbar from '../components/scrollbar';
// sections
import { StepByStepTable } from "../sections/@dashboard/attestations";
import {GeneralLayout} from "../layouts/GeneralLayout";
import Label from "../components/label";
import TextField from "@mui/material/TextField";
import React, {useEffect, useState} from "react";
import {useGetNextAttestationNumberQuery, usePostCreateAttestationMutation} from "../store/attestation/attestationApi";
import PageTitle from "../components/PageTitle";
import {CheckBox, MenuOpen} from "@mui/icons-material";
import copyObject from "../utils/copyObject";
import FormGroup from "@mui/material/FormGroup";

export default function CreateAttestationPage() {
    const {id} = useParams();
    const navigate = useNavigate();
    const [isSaving, setIsSaving] = useState(false);
    const [startDate, setStartDate] = useState(formatDate(new Date()));
    const [endDate, setEndDate] = useState(formatDate(new Date()));
    const {data} = useGetNextAttestationNumberQuery({courseId: id});
    const [createAttestation] = usePostCreateAttestationMutation();
    const [copyOfData, setCopyOfData] = useState(null);
    const [refresh, setRefresh] = useState(false);
    
    useEffect(() => {
        if (data === null || data === undefined)
            return;
        
        if (!data.success) {
            data.errors.map((e) => {
                return alert(e);
            })
        } else {
            setCopyOfData(copyObject(data));
        }
    }, [data]);
    
    function applyVisualChanges() {
        setRefresh(!refresh);
    }

    function formatDate(dateObj) {
        return `${dateObj.getFullYear()}-${withZero(dateObj.getMonth() + 1)}-${withZero(dateObj.getDate())}`;
    }

    function withZero(num) {
        return num < 10 ? `0${num}` : num;
    }

    const handleSave = async () => {
        setIsSaving(true);

        const request = {data: {
                courseId: id,
                startDate,
                endDate,
                manualGrades: copyOfData?.data?.disciplines?.map((disc) => {
                    return {
                        disciplineId: disc.disciplineId,
                        students: disc.students.filter((s) => s.manualGrade).map((s) => s.studentId)
                    }
                }) ?? []
            }};
        
        await createAttestation(request)
            .unwrap().then((fulfilled) => {
                if (fulfilled?.success) {
                    navigate('/facultyattestations');
                } else {
                    fulfilled.errors.map((item) => {
                        alert(item);
                        return 1;
                    });

                    setIsSaving(false);
                }
            }).catch(() => {
                setIsSaving(false);
                alert('Ошибка');
            });
    };
    
    function getStudentsWithGrade(discipline, grade) {
        return discipline.students.filter((s) => s.grade === grade && !s.manualGrade).length
    }
    
    function getNumberOfStudentsWithAllGrades(discipline) {
        return getStudentsWithGrade(discipline, 5) 
            + getStudentsWithGrade(discipline, 4) 
            + getStudentsWithGrade(discipline, 3) 
            + getStudentsWithGrade(discipline, 2);
    }
    
    return (
        <GeneralLayout>
            <PageTitle title={'Создать аттестацию'} />

            <Container>
                <Stack direction="column"
                       padding="120px 0px 0px 100px"
                       spacing={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                        <Typography variant="h4" gutterBottom>
                            {data === null || data === undefined ? <CircularProgress /> : <></>}
                            Создать аттестацию
                        </Typography>
                    </Stack>

                    <Card>
                        <CardContent>
                            <Stack spacing={2}>
                                <Typography><strong>Номер аттестации:</strong> {data?.data?.attestationNumber}</Typography>
                                
                                <Stack direction="row" spacing={2}>
                                    <Typography alignSelf="center">
                                        <strong>Дата начала аттестации</strong>
                                    </Typography>
                                    <TextField type="date" 
                                               value={startDate} 
                                               onChange={(event) => {setStartDate((event.target.value))}}/>
                                </Stack>

                                <Stack direction="row" spacing={2}>
                                    <Typography alignSelf="center">
                                        <strong>Дата окончания аттестации</strong>
                                    </Typography>
                                    <TextField type="date"
                                               value={endDate}
                                               onChange={(event) => {setEndDate((event.target.value))}}/>
                                </Stack>

                                <Stack spacing={2}>
                                    <Typography><strong>Дисциплины:</strong></Typography>
                                    {copyOfData?.data?.disciplines.sort((l,r) => l.disciplineName > r.disciplineName ? 1 : -1)
                                        .map((d) => {
                                        return <Paper elevation={3}>
                                            <Accordion>
                                                <AccordionSummary expandIcon={<MenuOpen/>}>
                                                    <Typography fontWeight={'bold'}>
                                                        {d.disciplineName}
                                                        {d.hasGrcStatements 
                                                            ? <> <Label color={'warning'}>Ведомость уже создана автоматически</Label></>
                                                            : <> {d.students.length === 0
                                                                ? <></>
                                                                : <Label color={'warning'}>Есть предлагаемые оценки</Label>}</>}
                                                    </Typography>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Stack spacing={2}>
                                                        {d.students.length === 0 || d.hasGrcStatements
                                                            ? <Typography>Нет предлагаемых оценок</Typography>
                                                            : <>
                                                                <Typography>Утвердите предлагаемые оценки для студентов:
                                                                    <Button size={'small'}
                                                                            onClick={() => {
                                                                                const newVal = d.students.length !== getNumberOfStudentsWithAllGrades(d);
                                                                                d.students.forEach((s) => {
                                                                                    s.manualGrade = !newVal;
                                                                                });

                                                                                applyVisualChanges();
                                                                            }}
                                                                            variant={'outlined'}>{d.students.length === getNumberOfStudentsWithAllGrades(d) ? 'Отклонить' : 'Утвердить'} все</Button></Typography>
                                                                <FormGroup>
                                                                    {
                                                                        d.students.sort((l,r) => l.studentFullName > r.studentFullName ? 1 : -1).map((s) => {
                                                                            return <FormControlLabel control={<Checkbox size={'small'}
                                                                                                                        onChange={(event, checked) => {s.manualGrade = !checked; applyVisualChanges();}}
                                                                                                                        checked={!s.manualGrade} />}
                                                                                                     label={<Typography fontSize={'small'}><strong>{s.studentFullName}</strong> - предлагаемая оценка <strong style={{'color': s.grade === 5 ? 'limegreen' : s.grade === 4 ? 'green' : s.grade === 3 ? 'orange' : 'red'}}>{s.grade}</strong> (баллов: {s.points})</Typography>} />
                                                                        })
                                                                    }
                                                                </FormGroup>

                                                                <Typography fontWeight={'bold'}>Итого по дисциплине:</Typography>
                                                                <Stack>
                                                                    <Typography fontSize={'small'}><strong>Оценка 5:</strong> {getStudentsWithGrade(d, 5)} чел.</Typography>
                                                                    <Typography fontSize={'small'}><strong>Оценка 4:</strong> {getStudentsWithGrade(d, 4)} чел.</Typography>
                                                                    <Typography fontSize={'small'}><strong>Оценка 3:</strong> {getStudentsWithGrade(d, 3)} чел.</Typography>
                                                                    <Typography fontSize={'small'}><strong>Оценка 2:</strong> {getStudentsWithGrade(d, 2)} чел.</Typography>
                                                                    <Typography fontSize={'small'}><strong>Без оценки:</strong> {d.students.length - getNumberOfStudentsWithAllGrades(d)} чел.</Typography>
                                                                </Stack>
                                                            </>}
                                                    </Stack>
                                                </AccordionDetails>
                                            </Accordion>
                                        </Paper>
                                    })}
                                </Stack>
                                
                                <Box height={10}/>
                                
                                <Stack justifyContent="space-between">
                                    {
                                        !isSaving
                                            ? <Button variant="contained"
                                                      disabled={data === null || data === undefined} 
                                                      onClick={handleSave}>Сохранить</Button>
                                            : <CircularProgress/>
                                    }
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                    <br />
                </Stack>
            </Container>
        </GeneralLayout>
    );
}